import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Copy, Loader2, Plane } from "lucide-react";

export default function Invest() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [wallets, setWallets] = useState({});
  const [tripTiers, setTripTiers] = useState([]);
  const [selected, setSelected] = useState(null); // package id
  const [amount, setAmount] = useState("");
  const [crypto, setCrypto] = useState("USDT_TRC20");
  const [txHash, setTxHash] = useState("");
  const [fromWallet, setFromWallet] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    api.get("/packages").then(({ data }) => {
      setPackages(data.packages);
      setWallets(data.wallets);
      setTripTiers(data.trip_tiers || []);
    });
  }, []);

  const pkg = packages.find((p) => p.id === selected);
  const willBeMultiplier = pkg
    ? (user?.referred_by ? pkg.multiplier_with_referral : pkg.multiplier_no_referral)
    : 0;

  const copyWallet = () => {
    navigator.clipboard.writeText(wallets[crypto] || "");
    toast.success("Wallet address copied");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pkg) return;
    setBusy(true);
    try {
      await api.post("/investments", {
        package_id: pkg.id,
        amount: parseFloat(amount),
        crypto,
        tx_hash: txHash.trim(),
        from_wallet: fromWallet.trim() || undefined,
      });
      toast.success("Investment submitted — awaiting admin approval");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07080a] text-slate-100 noise">
      <Navbar />
      <div className="pt-24 max-w-6xl mx-auto px-6 sm:px-10">
        <button data-testid="invest-back" onClick={() => nav("/dashboard")} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">New investment</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-10">
          Deploy capital <span className="text-gold italic">with intent.</span>
        </h1>

        {/* Steps */}
        <div className="flex items-center gap-4 mb-10 text-sm font-mono-x">
          {["Choose package", "Amount & payment", "Confirm"].map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${step > i ? "bg-gold text-black border-gold" : "text-slate-400 border-slate-700"}`}>{i + 1}</div>
              <span className={step > i ? "text-white" : "text-slate-500"}>{s}</span>
              {i < 2 && <div className="w-8 h-px bg-slate-700" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((p) => (
              <button
                key={p.id}
                data-testid={`select-pkg-${p.id}`}
                onClick={() => { setSelected(p.id); setStep(2); }}
                className={`card-elev card-hover text-left rounded-md p-8 relative overflow-hidden ${selected === p.id ? "border-gold" : ""}`}
              >
                <div className={`absolute -top-24 ${p.id === "premium" ? "-right-24 bg-gold/10" : "-left-24 bg-cyan-500/10"} w-64 h-64 rounded-full blur-3xl`} />
                <div className="relative">
                  <div className={`text-xs uppercase tracking-[0.3em] mb-3 ${p.id === "premium" ? "text-gold" : "text-cyan-300"}`}>{p.name}</div>
                  <div className="font-display text-3xl">{(p.daily_roi * 100).toFixed(1)}% Daily · {p.duration_days}d</div>
                  <div className="text-slate-400 mt-2">${p.min_amount.toLocaleString()} – ${p.max_amount.toLocaleString()}</div>
                  <div className="mt-6 text-slate-300 text-sm">{p.description}</div>
                  <div className="mt-6 font-mono-x text-xs text-slate-500">
                    Your multiplier: <span className={p.id === "premium" ? "text-gold" : "text-cyan-300"}>{user?.referred_by ? p.multiplier_with_referral : p.multiplier_no_referral}×</span>
                    {p.id === "premium" && !user?.referred_by && <span className="text-slate-500"> · Get 3× by joining via a referral</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && pkg && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="grid md:grid-cols-2 gap-6">
            <div className="card-elev rounded-md p-8">
              <div className="font-display text-2xl mb-6">Amount</div>
              <div>
                <Label className="text-slate-300">Investment amount (USD)</Label>
                <Input
                  data-testid="invest-amount"
                  type="number"
                  step="0.01"
                  min={pkg.min_amount}
                  max={pkg.max_amount}
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-2 bg-[#0b0c10] border-[#272a30] focus:border-gold focus:ring-gold text-white h-12 text-xl font-mono-x"
                  placeholder={`${pkg.min_amount}`}
                />
                <div className="text-xs text-slate-500 mt-2 font-mono-x">
                  Range ${pkg.min_amount.toLocaleString()} – ${pkg.max_amount.toLocaleString()}
                </div>
              </div>
              {/* Trip preview */}
              {(() => {
                const amt = parseFloat(amount || 0);
                let unlocked = null;
                let next = null;
                for (const t of tripTiers) {
                  if (amt >= t.threshold) unlocked = t;
                }
                for (const t of tripTiers) {
                  if (amt < t.threshold) { next = t; break; }
                }
                if (tripTiers.length === 0) return null;
                return (
                  <div data-testid="invest-trip-hint" className="mt-6 border border-gold/25 bg-gold/5 rounded-md p-4 flex items-start gap-3">
                    <Plane className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      {unlocked ? (
                        <div className="text-slate-200">
                          This investment unlocks <span className="text-gold font-semibold">{unlocked.name}</span> ({unlocked.destination}).
                          {next && <span className="text-slate-400"> Add <span className="font-mono-x text-gold">${(next.threshold - amt).toLocaleString()}</span> more to reach {next.name}.</span>}
                        </div>
                      ) : (
                        <div className="text-slate-300">
                          {next && <>Invest at least <span className="font-mono-x text-gold">${next.threshold.toLocaleString()}</span> to unlock <span className="text-gold font-semibold">{next.name}</span> — free trip.</>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-6">
                <Label className="text-slate-300">Crypto</Label>
                <select
                  data-testid="invest-crypto"
                  value={crypto}
                  onChange={(e) => setCrypto(e.target.value)}
                  className="mt-2 w-full bg-[#0b0c10] border border-[#272a30] rounded-md h-11 px-3 text-white focus:border-gold outline-none"
                >
                  <option value="USDT_TRC20">USDT (TRC20)</option>
                  <option value="BTC">Bitcoin</option>
                  <option value="ETH">Ethereum</option>
                </select>
              </div>
            </div>

            <div className="card-elev rounded-md p-8">
              <div className="font-display text-2xl mb-6">Payment address</div>
              <div className="text-slate-400 text-sm mb-4">
                Send exactly <span className="font-mono-x text-gold">${amount || "…"}</span> equivalent in {crypto} to the address below, then paste your transaction hash.
              </div>
              <div className="card-elev rounded-md p-4 flex items-center gap-3">
                <div className="flex-1 font-mono-x text-sm text-slate-200 overflow-hidden text-ellipsis break-all" data-testid="platform-wallet">
                  {wallets[crypto]}
                </div>
                <Button type="button" onClick={copyWallet} data-testid="copy-platform-wallet" className="bg-gold text-black hover:bg-[#f3e396]">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-6">
                <Label className="text-slate-300">Transaction hash</Label>
                <Input
                  data-testid="invest-tx-hash"
                  required
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  className="mt-2 bg-[#0b0c10] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11 font-mono-x"
                  placeholder="0xabc…"
                />
              </div>
              <div className="mt-4">
                <Label className="text-slate-300">Your sending wallet <span className="text-slate-500">(optional)</span></Label>
                <Input
                  data-testid="invest-from-wallet"
                  value={fromWallet}
                  onChange={(e) => setFromWallet(e.target.value)}
                  className="mt-2 bg-[#0b0c10] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11 font-mono-x"
                  placeholder="Your wallet"
                />
              </div>
              <div className="mt-8 flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="bg-transparent border-white/10 text-white hover:bg-white/5">Back</Button>
                <Button data-testid="invest-continue" type="submit" className="bg-gold text-black hover:bg-[#f3e396] flex-1 rounded-full">Continue</Button>
              </div>
            </div>
          </form>
        )}

        {step === 3 && pkg && (
          <form onSubmit={submit} className="card-elev rounded-md p-8 md:p-10 max-w-3xl">
            <div className="font-display text-2xl mb-6">Confirm submission</div>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <Row k="Package" v={pkg.name} />
              <Row k="Amount" v={`$${parseFloat(amount || 0).toLocaleString()}`} />
              <Row k="Daily ROI" v={`${(pkg.daily_roi * 100).toFixed(1)}%`} />
              <Row k="Duration" v={`${pkg.duration_days} days`} />
              <Row k="Multiplier" v={`${willBeMultiplier}× total`} highlight />
              <Row k="Target earned" v={`$${(parseFloat(amount || 0) * (willBeMultiplier - 1)).toLocaleString()}`} highlight />
              <Row k="Crypto" v={crypto} />
              <Row k="Tx hash" v={txHash} mono />
            </div>
            <div className="mt-8 flex items-start gap-3 text-slate-400 text-sm">
              <CheckCircle2 className="w-4 h-4 text-gold mt-0.5" />
              <div>Once submitted, an admin verifies your on-chain payment and activates your investment. Daily ROI starts from activation.</div>
            </div>
            <div className="mt-8 flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep(2)} className="bg-transparent border-white/10 text-white hover:bg-white/5">Back</Button>
              <Button data-testid="invest-submit" disabled={busy} className="bg-gold text-black hover:bg-[#f3e396] flex-1 rounded-full h-11">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit investment"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, mono, highlight }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono-x" : "font-display"} ${highlight ? "text-gold text-xl" : "text-white text-lg"} break-all`}>{v}</div>
    </div>
  );
}
